import { useState, useEffect } from 'react';
import { Menu, X, MessageCircle } from 'lucide-react';
import { useSiteConfig } from '../hooks/useSiteConfig';
import { openWhatsApp } from '../utils/whatsapp';
import { motion, AnimatePresence } from 'framer-motion';
import fallbackLogoIcon from '../assets/logo-icon.jpg';

export default function Header() {
  const siteConfig = useSiteConfig();
  const { navigation, contact, site } = siteConfig;
  const logoIcon = site.logoIcon || fallbackLogoIcon;
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ctaLabel, setCtaLabel] = useState('免費諮詢');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, contact.defaultMessage);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const targetId = href.slice(1);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-deep-navy/90 backdrop-blur-md border-b border-neon-cyan/15 shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center">
            <img src={logoIcon} alt={site.logo || 'Doudou'} className="h-16 md:h-20 w-auto rounded-lg object-contain" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7">
            {navigation.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-sm text-gray-300 hover:text-neon-cyan transition-colors duration-200 relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-0.5 after:bg-neon-cyan after:transition-all hover:after:w-full"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:block">
            <button
              onClick={handleCta}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple text-deep-navy font-semibold text-sm shadow-neon-cyan hover:brightness-110 transition-all duration-300"
            >
              <MessageCircle size={16} />
              {ctaLabel}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden px-4 pb-4"
          >
            <nav className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-3 space-y-1">
                {navigation.map((item) => (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="block py-3 px-4 text-gray-700 hover:text-neon-cyan hover:bg-gray-50 rounded-xl transition-colors font-medium"
                  >
                    {item.label}
                  </a>
                ))}
                <button
                  onClick={handleCta}
                  className="w-full flex items-center justify-center gap-2 mt-2 px-5 py-3 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple text-deep-navy font-semibold text-sm"
                >
                  <MessageCircle size={16} />
                  {ctaLabel}
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
